/**
 * StockPage — Parts catalogue management for Wadakaroo
 *
 * Features
 * ────────
 * • Paginated parts table sourced from GET /api/parts
 * • PartLookupPicker as the search / filter bar (make, model, name, mfr part ID)
 * • "Add Part" slide-over modal — POST /api/parts
 * • Activate / deactivate toggle — PATCH /api/parts/{uuid}/activate|deactivate
 * • Delete with confirm — DELETE /api/parts/{uuid}
 * • No Redux — all state is local to this page
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Package, Plus, AlertTriangle, ChevronLeft, ChevronRight, Check, X } from 'lucide-react';
import Layout from './Layout';
import PartLookupPicker from './common/PartLookupPicker';
import {
    listPartsApi,
    createPartApi,
    activatePartApi,
    deactivatePartApi,
    deletePartApi,
} from '../services/partsApi';

// ─── Tiny shared helpers ───────────────────────────────────────────────────────

const Spinner = ({ size = 5 }) => (
    <div className={`animate-spin rounded-full w-${size} h-${size} border-2 border-gray-200 border-t-orange-500`} />
);

const Badge = ({ active }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
    }`}>
        {active ? <Check size={10} /> : <X size={10} />}
        {active ? 'Active' : 'Inactive'}
    </span>
);

const Toast = ({ message, type = 'success', onClose }) => {
    useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
    return (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium
            ${type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
            {message}
            <button onClick={onClose} className="opacity-70 hover:opacity-100"><X size={14} /></button>
        </div>
    );
};

// ─── Create Part slide-over ────────────────────────────────────────────────────

const EMPTY_FORM = {
    name: '',
    manufacturerPartId: '',
    make: '',
    model: '',
    warrantyYears: '',
    cost: '',
};

const CreatePartSlideOver = ({ isOpen, onClose, onCreated }) => {
    const [form,    setForm]    = useState(EMPTY_FORM);
    const [saving,  setSaving]  = useState(false);
    const [error,   setError]   = useState(null);

    // Reset when re-opened
    useEffect(() => {
        if (isOpen) { setForm(EMPTY_FORM); setError(null); }
    }, [isOpen]);

    const set = (field, val) => setForm(p => ({ ...p, [field]: val }));

    const handleSubmit = async e => {
        e.preventDefault();
        if (!form.name.trim()) { setError('Part name is required.'); return; }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                name:               form.name.trim(),
                manufacturerPartId: form.manufacturerPartId.trim() || null,
                make:               form.make.trim()  || null,
                model:              form.model.trim() || null,
                warrantyYears:      form.warrantyYears !== '' ? parseInt(form.warrantyYears, 10) : null,
                cost:               form.cost          !== '' ? parseFloat(form.cost)            : null,
            };
            const created = await createPartApi(payload);
            onCreated(created);
            onClose();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to create part — please try again.');
        } finally {
            setSaving(false);
        }
    };

    const inputCls = "w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm " +
        "focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none";
    const labelCls = "block text-sm font-medium text-gray-700 mb-1.5";

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-sm transition-opacity duration-200 ${
                    isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                }`}
                onClick={onClose}
            />

            {/* Panel */}
            <div className={`fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-2xl
                             flex flex-col transition-transform duration-300 ease-out ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-orange-50 rounded-xl flex items-center justify-center">
                            <Package size={18} className="text-orange-600" />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900">Add Part</h2>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                        <X size={18} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                    <div className="px-6 py-6 space-y-5">

                        {/* Name */}
                        <div>
                            <label className={labelCls}>Part Name <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={e => set('name', e.target.value)}
                                placeholder="e.g. Brake Pads Front — Bosch"
                                required
                                className={inputCls}
                            />
                        </div>

                        {/* Mfr Part ID */}
                        <div>
                            <label className={labelCls}>Manufacturer Part ID</label>
                            <input
                                type="text"
                                value={form.manufacturerPartId}
                                onChange={e => set('manufacturerPartId', e.target.value)}
                                placeholder="e.g. BP-1234-F"
                                className={inputCls}
                            />
                        </div>

                        {/* Make + Model */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={labelCls}>Make</label>
                                <input
                                    type="text"
                                    value={form.make}
                                    onChange={e => set('make', e.target.value)}
                                    placeholder="e.g. Toyota"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className={labelCls}>Model</label>
                                <input
                                    type="text"
                                    value={form.model}
                                    onChange={e => set('model', e.target.value)}
                                    placeholder="e.g. Camry"
                                    className={inputCls}
                                />
                            </div>
                        </div>

                        {/* Cost + Warranty */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={labelCls}>Cost ($)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={form.cost}
                                    onChange={e => set('cost', e.target.value)}
                                    placeholder="0.00"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className={labelCls}>Warranty (years)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={form.warrantyYears}
                                    onChange={e => set('warrantyYears', e.target.value)}
                                    placeholder="—"
                                    className={inputCls}
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                                {error}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="inline-flex items-center gap-2 px-5 py-2 bg-orange-600 text-white text-sm
                                       font-semibold rounded-xl hover:bg-orange-700 disabled:opacity-60
                                       disabled:cursor-not-allowed transition-colors"
                        >
                            {saving && <Spinner size={4} />}
                            {saving ? 'Saving…' : 'Add Part'}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

// ─── Delete confirm modal ──────────────────────────────────────────────────────

const DeleteConfirm = ({ part, onConfirm, onCancel, isDeleting, error }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center shrink-0">
                    <AlertTriangle size={20} className="text-red-600" />
                </div>
                <div>
                    <h3 className="font-semibold text-gray-900">Delete Part</h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Delete <span className="font-medium text-gray-700">"{part.name}"</span>? This cannot be undone.
                    </p>
                </div>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex items-center justify-end gap-3 pt-2">
                <button onClick={onCancel}
                        className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900">
                    Cancel
                </button>
                <button onClick={onConfirm} disabled={isDeleting}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm
                                   font-semibold rounded-xl hover:bg-red-700 disabled:opacity-60 transition-colors">
                    {isDeleting && <Spinner size={4} />}
                    Delete
                </button>
            </div>
        </div>
    </div>
);

// ─── Main page ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const StockPage = () => {
    // ── Parts list state ──────────────────────────────────────────────────────
    const [parts,        setParts]        = useState([]);
    const [totalElements,setTotalElements]= useState(0);
    const [totalPages,   setTotalPages]   = useState(0);
    const [page,         setPage]         = useState(0);
    const [isLoading,    setIsLoading]    = useState(false);
    const [listError,    setListError]    = useState(null);

    // Active search filters committed from the picker
    const [activeFilters, setActiveFilters] = useState({
        make: '', model: '', name: '', manufacturerPartId: '',
    });

    // ── UI state ──────────────────────────────────────────────────────────────
    const [showCreate,   setShowCreate]   = useState(false);
    const [toDelete,     setToDelete]     = useState(null);   // part object
    const [isDeleting,   setIsDeleting]   = useState(false);
    const [deleteError,  setDeleteError]  = useState(null);
    const [toast,        setToast]        = useState(null);   // { message, type }
    const [toggling,     setToggling]     = useState({});     // uuid → true while in-flight

    // ── Fetch ──────────────────────────────────────────────────────────────────
    const loadPage = useCallback(async (filters, targetPage) => {
        setIsLoading(true);
        setListError(null);
        try {
            const data = await listPartsApi({
                ...filters,
                page: targetPage,
                size: PAGE_SIZE,
                activeOnly: false,
            });
            setParts(data.content ?? []);
            setTotalElements(data.totalElements ?? 0);
            setTotalPages(data.totalPages ?? 1);
            setPage(targetPage);
        } catch {
            setListError('Failed to load parts — please refresh.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Initial load
    useEffect(() => { loadPage(activeFilters, 0); }, []); // eslint-disable-line

    // ── Search from PartLookupPicker (used as search bar — selection ignored) ──
    // We intercept the picker's search by passing a no-op onChange and wiring
    // the search button in the picker to trigger loadPage via the filter state.
    // Instead, we use the picker in "search bar only" mode by reading the
    // committed filters from a search button at the page level.
    // (Simpler: we expose a separate onSearch prop from the picker variant used here.)

    const handleSearch = (filters) => {
        setActiveFilters(filters);
        loadPage(filters, 0);
    };

    // ── Create ────────────────────────────────────────────────────────────────
    const handleCreated = part => {
        setToast({ message: `"${part.name}" added to catalogue`, type: 'success' });
        loadPage(activeFilters, 0); // refresh list
    };

    // ── Toggle active ─────────────────────────────────────────────────────────
    const handleToggleActive = async part => {
        setToggling(p => ({ ...p, [part.uuid]: true }));
        try {
            const updated = part.active
                ? await deactivatePartApi(part.uuid)
                : await activatePartApi(part.uuid);
            setParts(prev => prev.map(p => p.uuid === updated.uuid ? updated : p));
            setToast({ message: `"${updated.name}" ${updated.active ? 'activated' : 'deactivated'}`, type: 'success' });
        } catch {
            setToast({ message: 'Failed to update status', type: 'error' });
        } finally {
            setToggling(p => { const n = { ...p }; delete n[part.uuid]; return n; });
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleConfirmDelete = async () => {
        if (!toDelete) return;
        setIsDeleting(true);
        setDeleteError(null);
        try {
            await deletePartApi(toDelete.uuid);
            setToast({ message: `"${toDelete.name}" deleted`, type: 'success' });
            setToDelete(null);
            loadPage(activeFilters, page);
        } catch (err) {
            setDeleteError(err?.response?.data?.message || 'Delete failed — please try again.');
        } finally {
            setIsDeleting(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <Layout>
            <div className="p-4 sm:p-6 space-y-6">

                {/* ── Page header ── */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                            <Package size={20} className="text-orange-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Parts Catalogue</h1>
                            <p className="text-sm text-gray-500">Search, manage and add parts</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowCreate(true)}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-orange-600 text-white
                                   font-semibold text-sm rounded-xl hover:bg-orange-700 transition-colors
                                   self-start sm:self-auto"
                    >
                        <Plus size={18} />
                        Add Part
                    </button>
                </div>

                {/* ── Search panel (PartLookupPicker as search bar — no selection) ── */}
                <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5">
                    <PartLookupPickerSearchBar onSearch={handleSearch} isLoading={isLoading} />
                </div>

                {/* ── Parts table ── */}
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    {listError && (
                        <div className="p-4 bg-red-50 border-b border-red-100 text-red-700 text-sm">
                            {listError}
                        </div>
                    )}

                    <div className="overflow-x-auto relative">
                        {isLoading && (
                            <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
                                <Spinner size={7} />
                            </div>
                        )}
                        <table className="w-full text-sm">
                            <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Mfr Part ID</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Make</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Model</th>
                                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Cost</th>
                                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Warranty</th>
                                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                <th className="px-4 py-3" />
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                            {!isLoading && parts.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center text-gray-400 text-sm">
                                        {Object.values(activeFilters).some(v => v)
                                            ? 'No parts match your search — try different terms.'
                                            : 'No parts in catalogue yet. Add your first part.'}
                                    </td>
                                </tr>
                            ) : (
                                parts.map(part => (
                                    <tr key={part.uuid}
                                        className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3.5 font-medium text-gray-900">
                                            {part.name}
                                        </td>
                                        <td className="px-4 py-3.5">
                                            {part.manufacturerPartId
                                                ? <span className="font-mono text-xs text-gray-600">{part.manufacturerPartId}</span>
                                                : <span className="text-gray-300">—</span>}
                                        </td>
                                        <td className="px-4 py-3.5 text-gray-600">
                                            {part.make || <span className="text-gray-300">—</span>}
                                        </td>
                                        <td className="px-4 py-3.5 text-gray-600">
                                            {part.model || <span className="text-gray-300">—</span>}
                                        </td>
                                        <td className="px-4 py-3.5 text-right font-medium text-gray-900">
                                            {part.cost != null
                                                ? `$${Number(part.cost).toFixed(2)}`
                                                : <span className="text-gray-300">—</span>}
                                        </td>
                                        <td className="px-4 py-3.5 text-center text-gray-600">
                                            {part.warrantyYears != null
                                                ? `${part.warrantyYears} yr${part.warrantyYears !== 1 ? 's' : ''}`
                                                : <span className="text-gray-300">—</span>}
                                        </td>
                                        <td className="px-4 py-3.5 text-center">
                                            <Badge active={part.active} />
                                        </td>
                                        <td className="px-4 py-3.5">
                                            <div className="flex items-center gap-1 justify-end">
                                                {/* Toggle active */}
                                                <button
                                                    onClick={() => handleToggleActive(part)}
                                                    disabled={!!toggling[part.uuid]}
                                                    title={part.active ? 'Deactivate' : 'Activate'}
                                                    className={`p-1.5 rounded-lg transition-colors ${
                                                        part.active
                                                            ? 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
                                                            : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50'
                                                    } disabled:opacity-40`}
                                                >
                                                    {toggling[part.uuid]
                                                        ? <Spinner size={4} />
                                                        : part.active
                                                            ? <X size={15} />
                                                            : <Check size={15} />}
                                                </button>
                                                {/* Delete */}
                                                <button
                                                    onClick={() => { setToDelete(part); setDeleteError(null); }}
                                                    title="Delete"
                                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination footer */}
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
                        <span>
                            {totalElements > 0
                                ? `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, totalElements)} of ${totalElements} parts`
                                : '0 parts'}
                        </span>
                        {totalPages > 1 && (
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => loadPage(activeFilters, page - 1)}
                                    disabled={page === 0 || isLoading}
                                    className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50
                                               disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <span className="px-3 text-xs font-medium text-gray-700">
                                    {page + 1} / {totalPages}
                                </span>
                                <button
                                    onClick={() => loadPage(activeFilters, page + 1)}
                                    disabled={page >= totalPages - 1 || isLoading}
                                    className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50
                                               disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Create slide-over ── */}
            <CreatePartSlideOver
                isOpen={showCreate}
                onClose={() => setShowCreate(false)}
                onCreated={handleCreated}
            />

            {/* ── Delete confirm ── */}
            {toDelete && (
                <DeleteConfirm
                    part={toDelete}
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setToDelete(null)}
                    isDeleting={isDeleting}
                    error={deleteError}
                />
            )}

            {/* ── Toast ── */}
            {toast && (
                <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
            )}
        </Layout>
    );
};

// ─── PartLookupPickerSearchBar ─────────────────────────────────────────────────
// A thin wrapper that uses the same search fields + button as PartLookupPicker
// but is wired to trigger a page-level search rather than a selection callback.
// Lives here (not in its own file) because it's only used on StockPage.

const EMPTY_SEARCH = { make: '', model: '', name: '', manufacturerPartId: '' };

const PartLookupPickerSearchBar = ({ onSearch, isLoading }) => {
    const [filters, setFilters] = useState(EMPTY_SEARCH);
    const set = (field, val) => setFilters(p => ({ ...p, [field]: val }));

    const handleSearch = () => onSearch(filters);
    const handleClear  = () => { setFilters(EMPTY_SEARCH); onSearch(EMPTY_SEARCH); };
    const handleKey    = e => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } };

    const hasFilters = Object.values(filters).some(v => v.trim());

    const inputCls = "w-full pl-8 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm " +
        "focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none " +
        "bg-white placeholder-gray-400";

    const Field = ({ icon, placeholder, field }) => (
        <div className="relative min-w-0">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400">
                {icon}
            </div>
            <input
                type="text"
                value={filters[field]}
                onChange={e => set(field, e.target.value)}
                onKeyDown={handleKey}
                placeholder={placeholder}
                className={inputCls}
            />
        </div>
    );

    return (
        <div className="flex flex-col lg:flex-row gap-3">
            {/* 4 search fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 flex-1 min-w-0">
                <Field field="make"  placeholder="Make (e.g. Toyota)" icon={
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                } />
                <Field field="model" placeholder="Model (e.g. Camry)" icon={
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10l2 1h8zm0 0l2 1h3a1 1 0 001-1V9a1 1 0 00-.293-.707l-3-3A1 1 0 0015 5h-2v11z" />
                    </svg>
                } />
                <Field field="name"  placeholder="Part name" icon={
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                } />
                <Field field="manufacturerPartId" placeholder="Mfr part ID" icon={
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                    </svg>
                } />
            </div>

            {/* Buttons */}
            <div className="flex gap-2 lg:shrink-0">
                <button
                    type="button"
                    onClick={handleSearch}
                    disabled={isLoading}
                    className="flex-1 lg:flex-none inline-flex items-center justify-center gap-2
                               px-5 py-2.5 bg-orange-600 text-white text-sm font-semibold rounded-xl
                               hover:bg-orange-700 disabled:opacity-60 disabled:cursor-not-allowed
                               transition-colors whitespace-nowrap"
                >
                    {isLoading
                        ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        )
                    }
                    {isLoading ? 'Searching…' : 'Search'}
                </button>
                {hasFilters && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="px-4 py-2.5 text-sm text-gray-500 hover:text-gray-700
                                   border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                        Clear
                    </button>
                )}
            </div>
        </div>
    );
};

export default StockPage;